import React, { useEffect, useState } from "react";
import { Info as InfoIcon } from "@material-ui/icons";
import Modal from "../../../components/common/Modal";
import _ from "lodash";
import {
  Button,
  Checkbox,
  EditableText,
  FlashMessage,
  Input,
} from "../../../components/common";
import { useFieldArray, useForm } from "react-hook-form";
import ChipInput from "material-ui-chip-input";
import RichTextEditor from "../../../components/common/RichTextEditor";
import { Grid, InputLabel } from "@material-ui/core";
import { stripHTMLTags } from "../../../utils/helper";
import { AsyncCreatableSelect } from "../../../components/common";
import commonServices from "../../../services/common.services";
import moment from "moment";
import {
  candidateUpdatedReset,
  updateCandidateRequest,
  uploadCandidateResumeReset,
} from "../../../reducers/candidate/candidate.reducer";
import { useDispatch, useSelector } from "react-redux";
import { ReducerFlag } from "../../../types/reducer.types";
import { fetchSessionDataRequest } from "../../../reducers/auth/session.reducer";

interface resumeModalProps {
  parsedModal: boolean;
  setParsedModal: (value: boolean) => void;
  userProfile: any;
  resumeData?: any;
  closeResumeModal?: () => void;
}

const ResumeModal = ({
  parsedModal,
  setParsedModal,
  userProfile,
  resumeData,
  closeResumeModal,
}: resumeModalProps) => {

  const {
    candidates: { updateLoading, updateProfileFlag, message },
  } = useSelector(({ candidate }: any) => candidate);
  // Support both full API response (data.parsed) and direct parsed object
  const source = _.get(resumeData, "parsed", resumeData) || {};
  const experiences = _.get(source, "job_experience", []);
  const skills = _.get(source, "skills", []);
  const education = _.get(source, "education", []);
  const projects = _.get(source, "projects", []);
  const [skillChip, setSubmitChip] = useState([]);
  const name = _.get(source, "name", "");
  const contact_info = _.get(source, "contact_info", "") || _.get(source, ["phone", 0], "");
  const [overRideFlag, setOverRideFlag] = useState<boolean>(false);
  const maxLimit = 1000;
  let searchDelay: NodeJS.Timeout;
  const dispatch = useDispatch();
  const { register, handleSubmit, control, watch, formState:{errors}, setValue, reset } = useForm({
    defaultValues: {
      experiences: experiences,
      skills: skills.map((skill) => typeof skill === 'string' ? { title: skill, description: null } : skill),
      projects: projects,
      education: education,
      nameChecked: false,
      contactChecked: false,
      experienceChecked: experiences.map(() => false),
      educationChecked: education.map(() => false),
      skillsChecked: false,
      projectChecked: projects.map(() => false),
      includeSkills: false,
      name: name,
      contact_info: contact_info,
    },
  });

  // When modal opens or parsed data changes, reset form so inputs show exact parsed values
  useEffect(() => {
    if (parsedModal && !_.isEmpty(source)) {
      const skillObjs = skills.map((s) => (typeof s === 'string' ? { title: s, description: null } : s));
      reset({
        experiences,
        skills: skillObjs,
        projects,
        education,
        nameChecked: false,
        contactChecked: false,
        experienceChecked: experiences.map(() => false),
        educationChecked: education.map(() => false),
        skillsChecked: false,
        projectChecked: projects.map(() => false),
        includeSkills: false,
        name,
        contact_info,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedModal, resumeData]);
  const experienceChecked = watch("experienceChecked");
  const educationChecked = watch("educationChecked");
  const skillsChecked = watch("includeSkills");
  const projectChecked = watch("projectChecked");
  const nameChecked = watch("nameChecked");
  const contactChecked = watch("contactChecked");
    
  const { fields: skillFields, append: skillAppend, remove: skillRemove } = useFieldArray({
    control,
    name: "skills",
  });

  useEffect(() => {
    if (!_.isEmpty(skills) && parsedModal) {
      
      const skillTitles = skills.map(skill => typeof skill === 'string' ? skill : skill.title || skill);
      setSubmitChip(skillTitles);
      
        
      // If field array is empty but we have skills, populate it
      if (skillFields.length === 0) {
        skills.forEach((skill) => {
          const skillObj = typeof skill === 'string' ? { title: skill, description: null } : skill;
          skillAppend(skillObj);
        });
      }
    }

    // eslint-disable-next-line
  }, [skills, parsedModal]);

  const _handleAddChip = (chip) => {
    const skillsArray: any = [...skillChip];
    skillsArray.push(chip);
    skillAppend({ title: chip, description: null });
    setSubmitChip(skillsArray);
  };

  const _handleDeleteChip = (chip, index) => {
    const skillsArray: any = [...skillChip];
    if (index > -1) {
      skillsArray.splice(index, 1);
      setSubmitChip(skillsArray);
      skillRemove(index);
    }
  };

  useEffect(() => {
    if ([ReducerFlag.SUCCESS, ReducerFlag.FAILED].includes(updateProfileFlag)) {
      if (updateProfileFlag === ReducerFlag.SUCCESS) {
           FlashMessage(message);
        setParsedModal(false);
        setOverRideFlag(false);
        dispatch(uploadCandidateResumeReset());
        dispatch(candidateUpdatedReset());
        dispatch(fetchSessionDataRequest());
        closeResumeModal?.();
      } else if (updateProfileFlag === ReducerFlag.FAILED) {
        FlashMessage(message, "error");
      }
    }
    // eslint-disable-next-line
  }, [updateProfileFlag]);

  const searchJobTitles = (search: string) =>
    new Promise((resolve) => {
      if (search !== "") {
        clearTimeout(searchDelay);
        searchDelay = setTimeout(async () => {
          const jobTitleDetails = await commonServices.fetchJobTitles(search);
          const locations = _.get(jobTitleDetails, "data", []).map(
            (c: any) => ({
              value: c.id,
              label: c.title,
            })
          );
          resolve(locations);
        }, 1000);
      } else {
        resolve([]);
      }
    });


  const onSubmit = (data) => {
    data?.experiences?.map((item) => {
      if (item.job_title_custom) {
        const jobCustomTitle = item.job_title_custom;
        if (item?.job_title_custom?.__isNew__) {
          return (item["jobTitle"] = {
            label: jobCustomTitle?.label
              ? jobCustomTitle.label
              : jobCustomTitle,
            value: jobCustomTitle?.value
              ? jobCustomTitle.value
              : jobCustomTitle,
            __isNew__: true,
          });
        } else {
          return (item["jobTitle"] = {
            label: jobCustomTitle?.label
              ? jobCustomTitle.label
              : jobCustomTitle,
            value: jobCustomTitle?.value
              ? jobCustomTitle.value
              : jobCustomTitle,
          });
        }
      } else {
        const jobTitle = item.jobTitle;
        return (item["jobTitle"] = {
          label: jobTitle?.label ? jobTitle.label : jobTitle,
          value: jobTitle?.value ? jobTitle.value : jobTitle,
        });
      }
    });
    const formData: any = {};

    const filteredExperiences = _.get(data, "experiences", []).filter(
      (_experience, index) => experienceChecked[index]
    );

    const filteredEducation = _.get(data, "education", []).filter(
      (_education, index) => educationChecked[index]
    );

    const filteredProjects = _.get(data, "projects", []).filter(
      (_project, index) => projectChecked[index]
    );

    if (!_.isEmpty(filteredExperiences)) {
      formData.experiences = filteredExperiences;
    }

    if (nameChecked) {
      const nameParts = data.name.split(" ");

      if (nameParts.length >= 2) {
        formData.first_name = nameParts[0];
        formData.last_name = nameParts.slice(1).join(" ");
      } else {
        formData.first_name = nameParts[0];
      }
    }

    if (contactChecked) {
      formData.contact_info = data.contact_info;
    }

    if (skillsChecked) {
      formData.skills = data.skills.map((skill) => { 
        return {
          title: typeof skill === 'string' ? skill : skill.title, 
          description: typeof skill === 'object' && skill.description ? skill.description : null
        }
      });
    }

    if (!_.isEmpty(filteredEducation)) {
      formData.education = filteredEducation;
    }

    if (!_.isEmpty(filteredProjects)) {
      formData.projects = filteredProjects;
    }
    if (overRideFlag) {
      formData.overRideFlag = overRideFlag;
    }
    console.log("formData", formData, 'data', data);
    dispatch(updateCandidateRequest(formData));
  };

  const _handlePresentCheckBoxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: any,
    field: string
  ) => {
    if(field === "experiences") {
      const { checked } = e.target;
    setValue(`experiences.${index}.is_present`, checked ? 1 : 0);
    console.log("checked", checked, experiences[index].is_present);
    if (checked === true) {
      setValue(
        `experiences.${index}.job_ended`,
        moment().format("YYYY-MM-DD")
      );
    } else {
      setValue(`experiences.${index}.job_ended`, "");
    }
  } else if(field === "projects") {
    const { checked } = e.target;
    setValue(`projects.${index}.is_present`, checked ? 1 : 0);
    if (checked === true) {
      setValue(`projects.${index}.end_date`, moment().format("YYYY-MM-DD"));
    } else {
      setValue(`projects.${index}.end_date`, "");
    }
  } else if(field === "education") {
    const { checked } = e.target;
    setValue(`education.${index}.is_present`, checked ? 1 : 0);
    if (checked === true) {
      setValue(`education.${index}.degree_to`, moment().format("YYYY-MM-DD"));
    } else {
      setValue(`education.${index}.degree_to`, "");
    }
  }
  };

  return (
    <Modal
      visible={parsedModal}
      onClose={() => {
        dispatch(uploadCandidateResumeReset());
        setParsedModal(false);
        closeResumeModal?.();
        setOverRideFlag(false);
      }}
      size="x-large"
      className="resume-modal"
      title="Update Profile"
    >
      <div className="d-flex">
        <InfoIcon className="font-md-2 text-primary" />{" "}
        <p className="ml-1">
          Our resume parsing feature is currently in the beta stage. While
          efforts are made for accuracy, we advise users to carefully review
          parsed information and make any necessary adjustments as we work
          towards improvement.
        </p>
      </div>
      <div className="d-flex mt-3">
        <InfoIcon className="font-md-2 text-primary" />{" "}
        <p className="ml-1">
          Mark the information you want to Override or Append with your profile
        </p>
      </div>
      <hr className="my-3" />
      <div className="modal-head">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <div className="flex">
              <Checkbox
                externalLabel={{
                  label: <strong>Name:</strong>,
                }}
                defaultChecked={resumeData?.nameChecked || false}
                {...register(`nameChecked`)}
              />{" "}
              <div className="d-flex user-name">
                {/* <EditableText
                  name="name"
                  inputClassName="editable-input"
                  control={control}
                  textProps={{
                    className: "m-0 font-md",
                  }}
                  country="US"
                  error={errors}
                  rules={{
                    required: { value: true, message: "Name is required" },
                    maxLength: {
                      value: 20,
                      message: "Name must be between 2 to 20 characters long",
                    },
                    minLength: {
                      value: 2,
                      message: "Name must be between 2 to 20 characters long",
                    },
                  }}
                  defaultValue={name}
                /> */}
                <EditableText
                  name="name"
                  inputClassName="editable-input"
                  control={control}
                  textProps={{
                    className: "m-0 font-md",
                  }}
                  country="US"
                  error={errors}
                  rules={{
                    validate: {
                      required: (value) =>
                        nameChecked ? !!value || "Name is required" : true,
                      maxLength: (value) =>
                        nameChecked
                          ? value?.length <= 20 ||
                            "Name must be between 2 to 20 characters long"
                          : true,
                      minLength: (value) =>
                        nameChecked
                          ? value?.length >= 2 ||
                            "Name must be between 2 to 20 characters long"
                          : true,
                    },
                  }}
                  defaultValue={name}
                />
              </div>
            </div>
            {errors?.name && (
              <p className="error-message-name">{errors?.name?.message as string}</p>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <div className="flex">
              <Checkbox
                externalLabel={{
                  label: <b>Contact:</b>,
                }}
                defaultChecked={resumeData?.contactChecked || false}
                {...register(`contactChecked`)}
              />
              {/* <EditableText
                inputClassName="editable-input"
                control={control}
                defaultValue={_.get(
                  resumeData,
                  "contact_info",
                  userProfile.contact_info
                )}
                name="contact_info"
                country="us"
                error={errors}
                rules={{
                  required: {
                    value: true,
                    message: "Phone is required",
                  },
                }}
              /> */}
              <EditableText
                inputClassName="editable-input"
                control={control}
                defaultValue={contact_info || userProfile?.contact_info}
                name="contact_info"
                country="us"
                error={errors}
                rules={{
                  validate: {
                    required: (value) =>
                      contactChecked ? !!value || "Phone is required" : true,
                  },
                }}
              />
            </div>
            {errors?.contact_info && (
              <p className="error-message-contact">{errors?.contact_info?.message as string}</p>
            )}
          </Grid>
        </Grid>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="edit-modal-wrapper">
        <div className="body-wrapper">
          {!_.isEmpty(experiences) && (
            <div className="modal-content">
              <h5>Experience</h5>
              <div className="experience-row">
                {experiences.map((item, index) => {
                  return (
                    <div
                      className="career-differentiators-form-item"
                      key={index}
                    >
                      <div className="career-differentiators-row">
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <div className="date-fields w-100">
                              <AsyncCreatableSelect
                                name={`experiences.${index}.jobTitle`}
                                control={control}
                                validationObj={errors}
                                placeholder="Search Job..."
                                externalLabel={{
                                  label: (
                                    <div className="title-checkbox">
                                      {" "}
                                      <Checkbox
                                        {...register(`experienceChecked.${index}`)}
                                        defaultChecked={experienceChecked[index]}
                                        externalLabel={{
                                          label: `Job Title`,
                                        }}
                                      />
                                    </div>
                                  ),
                                  // className: "required",
                                }}
                                defaultValue={item?.jobTitle}
                                onStartSearching={searchJobTitles}
                                noOptionsMessage="No matching job found"
                                disabled={!watch(`experienceChecked.${index}`)}
                                rules={{
                                  validate: (value) => {
                                    if (experienceChecked[index]) {
                                      if (!value || (!value.label && !value.value)) {
                                        return "Job title is required when checkbox is selected";
                                      }
                                    }
                                    return true;
                                  }
                                }}
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <div className="date-fields w-100">
                              <Input
                                placeholder="Name"
                                externalLabel={{ label: "Company Name" }}
                                validationObj={errors}
                                defaultValue={experiences?.[index].company_name}
                                required={!!experienceChecked[index]}
                                {...register(`experiences.${index}.company_name`, {
                                  validate: (value) => {
                                    if (experienceChecked[index]) {
                                      if (!value || value.trim() === "") {
                                        return "Company name is required when checkbox is selected";
                                      }
                                    }
                                    return true;
                                  }
                                })}
                                disabled={!watch(`experienceChecked.${index}`)}
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <div className="date-input">
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <div className="date-fields w-100">
                                    <Input
                                      externalLabel={{ label: "Select Date" }}
                                      placeholder="Work Experience"
                                      type="date"
                                      defaultValue={
                                        experiences?.[index].job_started
                                      }
                                      validationObj={errors}
                                      required={!!experienceChecked[index]}
                                      {...register(`experiences.${index}.job_started`, {
                                        validate: (value) => {
                                          if (experienceChecked[index]) {
                                            if (!value || value.trim() === "") {
                                              return "Start date is required when checkbox is selected";
                                            }
                                          }
                                          return true;
                                        }
                                      })}
                                      disabled={!watch(`experienceChecked.${index}`)}
                                    />
                                  </div>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <div className="date-fields edit-education-present w-100">
                                    <Checkbox
                                      onChange={(e) =>
                                        _handlePresentCheckBoxChange(e, index, "experiences")
                                      }
                                      control={control}
                                      externalLabel={{ label: "Present" }}
                                      defaultChecked={
                                        experiences[index].is_present === 1
                                      }
                                      name={`experiences.${index}.is_present`}
                                      disabled={!watch(`experienceChecked.${index}`)}
                                    />

                                    {watch(`experiences.${index}.is_present`) ? (
                                      <InputLabel className="present-label">
                                        Present
                                      </InputLabel>
                                    ) : (
                                      <Input
                                        placeholder="early career"
                                        type="date"
                                        defaultValue={
                                          experiences[index].job_ended
                                        }
                                        validationObj={errors}
                                        required={!!(experienceChecked[index] && !watch(`experiences.${index}.is_present`))}
                                        {...register(`experiences.${index}.job_ended`, {
                                          validate: (value) => {
                                            if (experienceChecked[index] && !watch(`experiences.${index}.is_present`)) {
                                              if (!value || value.trim() === "") {
                                                return "End date is required when checkbox is selected (or mark as present)";
                                              }
                                            }
                                            return true;
                                          }
                                        })}
                                        disabled={!watch(`experienceChecked.${index}`)}
                                      />
                                    )}
                                  </div>
                                </Grid>
                              </Grid>
                            </div>
                          </Grid>
                          <Grid item xs={12} md={12}>
                            <div className="rich-text-editor-job">
                              <RichTextEditor
                                name={`experiences.${index}.description`}
                                placeholder="Job Description"
                                externalLabel={{ label: "Job Description" }}
                                defaultValue={experiences[index]?.description}
                                validationObj={errors}
                                control={control}
                                required={!!experienceChecked[index]}
                                rules={{
                                  validate: {
                                    required: (value) => {
                                      if (!experienceChecked[index]) return true;
                                      const tmpText = stripHTMLTags(value || "");
                                      return tmpText.trim().length > 0 || "Please enter a valid job Description";
                                    },
                                    validLength: (value) => {
                                      const tmpText = stripHTMLTags(
                                        value || ""
                                      );
                                      return (
                                        tmpText.length <= maxLimit ||
                                        `Please enter less than ${maxLimit} characters`
                                      );
                                    },
                                  },
                                }}
                                showCount
                                maxLimit={maxLimit}
                                disabled={!watch(`experienceChecked.${index}`)}
                              />
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Projects */}
          {!_.isEmpty(projects) && (
            <div className="modal-content">
              <h5>Projects</h5>
              <div className="project-row">
                {projects.map((item, index) => (
                  <div key={index} className="career-differentiators-form-item">
                    <div className="career-differentiators-row">
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <div>
                            <Input
                              placeholder="Project Title"
                              externalLabel={{
                                label: (
                                  <div className="title-checkbox">
                                    {" "}
                                    <Checkbox
                                      externalLabel={{
                                        label: `Project Title`,
                                      }}
                                      {...register(`projectChecked.${index}`)}
                                      defaultChecked={projectChecked[index]}
                                    />
                                  </div>
                                ),
                              }}
                              defaultValue={item.title}
                              validationObj={errors}
                              required={!!projectChecked[index]}
                              {...register(`projects.${index}.title`, {
                                validate: (value) => {
                                  if (projectChecked[index]) {
                                    if (!value || value.trim() === "") {
                                      return "Project title is required when checkbox is selected";
                                    }
                                  }
                                  return true;
                                }
                              })}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <div>
                            <Input
                              placeholder="Project URL"
                              externalLabel={{ label: "Project URL" }}
                              validationObj={errors}
                              defaultValue={item.link}
                              required={!!projectChecked[index]}
                              {...register(`projects.${index}.link`, {
                                validate: (value) => {
                                  if (projectChecked[index]) {
                                    if (!value || value.trim() === "") {
                                      return "Project URL is required when checkbox is selected";
                                    }
                                    // Validate URL format
                                    const urlPattern = /[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/g;
                                    if (!urlPattern.test(value)) {
                                      return "Please enter a valid Project URL";
                                    }
                                  }
                                  return true;
                                }
                              })}
                              
                              disabled={!watch(`projectChecked.${index}`)}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <div className="date-input">
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <div className="date-fields">
                                  <Input
                                    externalLabel={{ label: "Select Date" }}
                                    placeholder="Work Experience"
                                    type="date"
                                    defaultValue={item.start_date}
                                    validationObj={errors}
                                    required={!!projectChecked[index]}
                                    {...register(`projects.${index}.start_date`, {
                                      validate: (value) => {
                                        if (projectChecked[index]) {
                                          if (!value || value.trim() === "") {
                                            return "Start date is required when checkbox is selected";
                                          }
                                        }
                                        return true;
                                      }
                                    })}
                                    disabled={!watch(`projectChecked.${index}`)}
                                  />
                                </div>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <div className="date-fields edit-education-present">
                                  <Checkbox
                                  name={`projects.${index}.is_present`}
                                  defaultChecked={
                                    projects[index]?.is_present === 1
                                  }
                                    onChange={(e) =>
                                      _handlePresentCheckBoxChange(e, index, "projects")
                                    }
                                    externalLabel={{ label: "Present" }}
                                    disabled={!watch(`projectChecked.${index}`)}
                                  />
                                  {watch(`projects.${index}.is_present`) ? (
                                    <InputLabel className="present-label">
                                      Present
                                    </InputLabel>
                                  ) : (
                                    <Input
                                      defaultValue={item.end_date}
                                      placeholder="End date"
                                      type="date"
                                      validationObj={errors}
                                      required={!!(projectChecked[index] && !watch(`projects.${index}.is_present`))}
                                      {...register(`projects.${index}.end_date`, {
                                        validate: (value) => {
                                          if (projectChecked[index] && !watch(`projects.${index}.is_present`)) {
                                            if (!value || value.trim() === "") {
                                              return "End date is required when checkbox is selected (or mark as present)";
                                            }
                                          }
                                          return true;
                                        }
                                      })}
                                      disabled={!watch(`projectChecked.${index}`)}
                                    />
                                  )}
                                </div>
                              </Grid>
                            </Grid>
                          </div>
                        </Grid>
                        <Grid item xs={12} md={12}>
                          <div className="rich-text-editor">
                            <RichTextEditor
                              name={`projects.${index}.description`}
                              externalLabel={{
                                label: "Project Description",
                              }}
                              defaultValue={item.description}
                              placeholder="Project Description"
                              validationObj={errors}
                              control={control}
                              rules={{
                                validate: {
                                  required: (value) => {
                                    if (!projectChecked[index]) return true;
                                    const tmpText = stripHTMLTags(value || "");
                                    return tmpText.trim().length > 0 || "Please enter a valid Project Description";
                                  },
                                  validLength: (value) => {
                                    const tmpText = stripHTMLTags(value || "");
                                    return (
                                      tmpText.length <= maxLimit ||
                                      `Please enter less than ${maxLimit} characters`
                                    );
                                  },
                                },
                              }}
                              showCount
                              maxLimit={maxLimit}
                                disabled={!watch(`projectChecked.${index}`)}
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Education */}
          {!_.isEmpty(education) && (
            <div className="modal-content">
              <h5>Education</h5>
              <div className="education-row">
                {education.map((item, index) => (
                  <div
                    key={index}
                    className="career-differentiators-form-item  education-form-item"
                  >
                    <div className="career-differentiators-row education-form-row">
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <div>
                            <Input
                              placeholder="Name"
                              externalLabel={{
                                label: (
                                  <div className="title-checkbox">
                                    {" "}
                                    <Checkbox
                                      externalLabel={{
                                        label: `Institution Name`,
                                      }}
                                      {...register(`educationChecked.${index}`)}
                                      defaultChecked={educationChecked[index]}
                                    />
                                  </div>
                                ),
                              }}
                              defaultValue={item.institute_name}
                              validationObj={errors}
                              required={!!educationChecked[index]}
                              {...register(`education.${index}.institute_name`, {
                                validate: (value) => {
                                  if (educationChecked[index]) {
                                    if (!value || value.trim() === "") {
                                      return "Institution name is required when checkbox is selected";
                                    }
                                  }
                                  return true;
                                }
                              })}
                              disabled={!watch(`educationChecked.${index}`)}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <div className="date-input">
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <div>
                                  <Input
                                    externalLabel={{ label: "Select Date" }}
                                    placeholder="Start Date"
                                    type="date"
                                    defaultValue={item.degree_from}
                                    validationObj={errors}
                                    required={!!educationChecked[index]}
                                    {...register(`education.${index}.degree_from`, {
                                      validate: (value) => {
                                        if (educationChecked[index]) {
                                          if (!value || value.trim() === "") {
                                            return "Start date is required when checkbox is selected";
                                          }
                                        }
                                        return true;
                                      }
                                    })}
                                    disabled={!watch(`educationChecked.${index}`)}
                                  />
                                </div>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <div className="edit-education-present">
                                  <Checkbox
                                   name={`education.${index}.is_present`}
                                   defaultChecked={
                                     education[index]?.is_present === 1
                                   }
                                    onChange={(e) =>
                                      _handlePresentCheckBoxChange(e, index, "education")
                                    }
                                    externalLabel={{ label: "Present" }}
                                    disabled={!watch(`educationChecked.${index}`)}
                                  />
                                  {watch(`education.${index}.is_present`) ? (
                                    <InputLabel className="present-label">
                                      Present
                                    </InputLabel>
                                  ) : (
                                    <Input
                                      defaultValue={item.degree_to}
                                      placeholder="End date"
                                      type="date"
                                      validationObj={errors}
                                      required={!!(educationChecked[index] && !watch(`education.${index}.is_present`))}
                                      {...register(`education.${index}.degree_to`, {
                                        validate: (value) => {
                                          if (educationChecked[index] && !watch(`education.${index}.is_present`)) {
                                            if (!value || value.trim() === "") {
                                              return "End date is required when checkbox is selected (or mark as present)";
                                            }
                                          }
                                          return true;
                                        }
                                      })}
                                        disabled={!watch(`educationChecked.${index}`)}
                                    />
                                  )}
                                </div>
                              </Grid>
                            </Grid>
                          </div>
                        </Grid>
                       
                      </Grid>
                    </div>

                    <div className="career-differentiators-row education-form-row">
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <div>
                            <Input
                              placeholder="Field of study"
                              externalLabel={{ label: "Field of Study" }}
                              validationObj={errors}
                              defaultValue={item.field_of_study}
                              required={!!educationChecked[index]}
                              {...register(`education.${index}.field_of_study`, {
                                validate: (value) => {
                                  if (educationChecked[index]) {
                                    if (!value || value.trim() === "") {
                                      return "Field of study is required when checkbox is selected";
                                    }
                                  }
                                  return true;
                                }
                              })}
                                disabled={!watch(`educationChecked.${index}`)}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <div>
                            <Input
                              placeholder="Degree"
                              externalLabel={{ label: "Degree" }}
                              validationObj={errors}
                              defaultValue={item.degree_title}
                                required={!!educationChecked[index]}
                              {...register(`education.${index}.degree_title`, {
                                validate: (value) => {
                                  if (educationChecked[index]) {
                                    if (!value || value.trim() === "") {
                                      return "Degree is required when checkbox is selected";
                                    }
                                  }
                                  return true;
                                }
                              })}
                              disabled={!watch(`educationChecked.${index}`)}
                            />
                          </div>
                        </Grid>
                      <Grid item xs={12} md={12}>
                          <div className="rich-text-editor">
                            <RichTextEditor
                              name={`education.${index}.description`}
                              externalLabel={{
                                label: "Education Description",
                              }}
                              defaultValue={item.description}
                              placeholder="Education Description"
                              validationObj={errors}
                              control={control}
                              rules={{
                                validate: {
                                  required: (value) => {
                                    if (!educationChecked[index]) return true;
                                    const tmpText = stripHTMLTags(value || "");
                                    return tmpText.trim().length > 0 || "Please enter a valid Education Description";
                                  },
                                  validLength: (value) => {
                                    const tmpText = stripHTMLTags(value || "");
                                    return (
                                      tmpText.length <= maxLimit ||
                                      `Please enter less than ${maxLimit} characters`
                                    );
                                  },
                                },
                              }}
                              showCount
                              maxLimit={maxLimit}
                              disabled={!watch(`educationChecked.${index}`)}
                            />
                          </div>
                      </Grid>
                      </Grid>

                    </div>
                    
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* skills */}
          {!_.isEmpty(skills) && (
            <div className="modal-content">
              <Checkbox
                externalLabel={{ label: "Skills" }}
                {...register(`includeSkills`)}
                defaultChecked={skillsChecked}
              />
              <h4>Skills</h4>
              <div className="skill-row">
                <ChipInput
                  variant="outlined"
                  placeholder="Add skills"
                  value={skillChip}
                  onAdd={(chip) => _handleAddChip(chip)}
                  onDelete={(chip, index) => _handleDeleteChip(chip, index)}
                  className="w-100"
                  disabled={!skillsChecked}
                />
              </div>
            </div>
          )}
          <div className="submit-button">
            <Button
              type={"submit"}
              variant="text"
              color="secondary"
              onClick={() => setOverRideFlag(true)}
              loading={updateLoading}
            >
              Override Existing Profile
            </Button>
            <Button type={"submit"} loading={updateLoading} onClick={() => setOverRideFlag(false)}>
              Append to Existing Profile
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ResumeModal;
